import React from "react";
import { isAxiosError } from "axios";

import Button from "../../../shared/ui/Button";
import Modal from "../../../shared/ui/Modal";
import { getErrorMessage } from "../../../shared/api/helpers";
import { toast } from "../../../shared/ui/toast";
import { useAuth } from "../../auth/AuthContext";
import { createItemGroupFn } from "../item-groups.api";
import type { ItemGroupSummary } from "../item-groups.api";
import { validateQuickItemGroupName } from "./quickItemGroup";

interface QuickItemGroupCreateModalProps {
  show: boolean;
  onHide: () => void;
  onCreated: (group: ItemGroupSummary) => void;
}

const firstErrorMessage = (value: unknown): string | null => {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    return value.find((item): item is string => typeof item === "string") ?? null;
  }
  return null;
};

const QuickItemGroupCreateModal = ({ show, onHide, onCreated }: QuickItemGroupCreateModalProps) => {
  const auth = useAuth();
  const [name, setName] = React.useState("");
  const [nameError, setNameError] = React.useState<string | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!show) return;
    setName("");
    setNameError(null);
    setErrorMessage(null);
  }, [show]);

  const close = () => {
    if (isSubmitting) return;
    onHide();
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const validationError = validateQuickItemGroupName(name);
    if (validationError) {
      setNameError(validationError);
      return;
    }

    setNameError(null);
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      const created = await createItemGroupFn({ name: name.trim() });
      onCreated(created);
      toast.success(`상품그룹 "${created.name}"을 등록했습니다.`);
      onHide();
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response?.status === 401) {
        await auth.logout();
        return;
      }
      const fieldMessage = isAxiosError(error)
        ? firstErrorMessage(error.response?.data?.name)
        : null;
      if (fieldMessage) setNameError(fieldMessage);
      else setErrorMessage(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={close} size="sm" backdrop="static">
      <form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>새 상품그룹</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {errorMessage && <div className="alert alert-danger" role="alert">{errorMessage}</div>}
          <label htmlFor="quick-item-group-name" className="form-label">그룹명</label>
          <input
            id="quick-item-group-name"
            type="text"
            className={`form-control ${nameError ? "is-invalid" : ""}`}
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={100}
            autoFocus
            disabled={isSubmitting}
          />
          {nameError && <div className="invalid-feedback">{nameError}</div>}
          <div className="form-text">그룹 우선순위는 기본값 50으로 등록됩니다.</div>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={close} disabled={isSubmitting}>취소</Button>
          <Button type="submit" disabled={isSubmitting}>{isSubmitting ? "등록 중..." : "등록"}</Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default QuickItemGroupCreateModal;
